/**
 * Parser for questionnaire structure - creates a hierarchical tree for navigation
 */

/**
 * Types of nodes in the structure tree
 */
export enum NodeType {
  SURVEY = "Survey",
  SECTION = "Section",
  QUESTION = "Question",
  LIST = "List",
  RANGE_LIST = "Range List",
  EXCEPTIONS = "Exceptions",
  EXCEPTION_ITEM = "Exception Item",
  TEXT = "Text",
  NOTE = "Note",
  LOGIC = "Logic",
}

/**
 * Represents an item within EXCEPTIONS
 */
export interface ExceptionItem {
  text: string
  usageCount: number
  isUnused: boolean
}

/**
 * Represents a node in the structure tree
 */
export interface StructureNode {
  id: string
  type: NodeType
  name: string
  lineNumber: number
  endLineNumber?: number // For capturing complete element boundaries
  children?: StructureNode[]
  metadata?: {
    // For lists
    itemCount?: number
    referenceCount?: number
    isUnused?: boolean

    // For EXCEPTIONS items
    items?: ExceptionItem[]

    // For sections
    questionCount?: number
    textBlockCount?: number
    noteCount?: number

    // For questions
    listReference?: string

    // Raw content lines for type detection
    contentLines?: string[]

    // Placeholder detection (for lists and questions)
    isPlaceholder?: boolean
  }
}

/**
 * Parses questionnaire content into a hierarchical structure tree
 *
 * @param content - The questionnaire content as a string
 * @returns The root structure node containing the full tree
 */
export const parseQuestionnaireStructure = (content: string): StructureNode => {
  const lines = content.split("\n")

  // Root node
  const root: StructureNode = {
    id: "root",
    type: NodeType.SURVEY,
    name: "Questionnaire",
    lineNumber: 1,
    children: [],
  }

  let currentSection: StructureNode | null = null
  const allLists: StructureNode[] = []
  const exceptionsPhrases: string[] = []

  // Helper to detect if a line is a major keyword that starts a new element
  const isMajorKeywordStart = (line: string): boolean => {
    const trimmed = line.trim()
    return !!(
      trimmed.match(/^(SURVEY|SECTION|Q\s|TEXT\s|NOTE\s|LIST\s|RANGE LIST\s)/) ||
      trimmed === "LIST EXCEPTIONS" ||
      trimmed === "EXCEPTIONS"
    )
  }

  // Helper to find where an element ends
  const findElementEnd = (startIndex: number): number => {
    let endIndex = startIndex
    let hitFirstEmptyLine = false

    for (let j = startIndex + 1; j < lines.length; j++) {
      const nextLine = lines[j].trim()

      // Empty line - mark that we hit one
      if (!nextLine) {
        if (!hitFirstEmptyLine) {
          hitFirstEmptyLine = true
          endIndex = j - 1 // End at the line before the empty line
        }
        continue
      }

      // If we already hit an empty line, stop at next content
      if (hitFirstEmptyLine) {
        break
      }

      // Comments (#) after content should NOT be part of the element
      if (nextLine.startsWith("#")) {
        break
      }

      // Scripting logic (IF, BEGIN, END, etc.) should be separate elements
      if (nextLine.match(/^(IF\s|BEGIN|END|TERMINATE|FOR EACH|RANDOMIZE|SKIP TO|VALIDATE|HIDE|HIDDEN)/)) {
        break
      }

      // Another major keyword starts - this is where we end
      if (isMajorKeywordStart(nextLine)) {
        break
      }

      // Regular content line continues the element
      endIndex = j
    }
    return endIndex
  }

  // First pass: Parse structure with boundaries
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmedLine = line.trim()
    const lineNumber = i + 1

    // Skip empty lines and comments
    if (!trimmedLine || trimmedLine.startsWith("#")) {
      continue
    }

    // SURVEY title
    const surveyMatch = trimmedLine.match(/^SURVEY\s+(.+)/)
    if (surveyMatch) {
      root.name = surveyMatch[1].trim()
      root.lineNumber = lineNumber
      continue
    }

    // SECTION
    const sectionMatch = trimmedLine.match(/^SECTION\s+(.+)/)
    if (sectionMatch) {
      const sectionName = sectionMatch[1].trim()
      const endLine = findElementEnd(i)
      currentSection = {
        id: `section_${lineNumber}`,
        type: NodeType.SECTION,
        name: sectionName,
        lineNumber,
        endLineNumber: endLine + 1,
        children: [],
        metadata: {
          questionCount: 0,
          textBlockCount: 0,
          noteCount: 0,
        },
      }
      root.children!.push(currentSection)
      continue
    }

    // QUESTION (Q Label:) - allow template placeholders like {{question label}}
    const questionMatch = trimmedLine.match(/^Q\s+(.+?):/)
    if (questionMatch) {
      const questionLabel = questionMatch[1].trim()
      const endLine = findElementEnd(i)

      // Get content lines for this question
      const contentLines = lines.slice(i, endLine + 1)

      // Check if question label contains PLACEHOLDER or if any inline response option contains PLACEHOLDER
      // (but exclude list references like "FROM PLACEHOLDER" - only check actual response text)
      const hasPlaceholder = questionLabel.toUpperCase().includes('PLACEHOLDER') ||
        contentLines.some(line => {
          const trimmed = line.trim()
          // Skip FROM/PER list references
          if (trimmed.match(/^(FROM|PER)\s+/i)) return false
          // Check actual response options and other content
          return trimmed.toUpperCase().includes('PLACEHOLDER')
        })

      const questionNode: StructureNode = {
        id: `question_${lineNumber}`,
        type: NodeType.QUESTION,
        name: questionLabel,
        lineNumber,
        endLineNumber: endLine + 1,
        metadata: {
          contentLines,
          isPlaceholder: hasPlaceholder,
        },
      }

      // Extract list reference (FROM or PER)
      const fromMatch = line.match(/FROM\s+([A-Za-z0-9_\s]+)/)
      const perMatch = line.match(/PER\s+([A-Za-z0-9_\s]+)/)

      if (fromMatch) {
        questionNode.metadata!.listReference = fromMatch[1].trim()
      } else if (perMatch) {
        questionNode.metadata!.listReference = perMatch[1].trim()
      }

      if (currentSection) {
        currentSection.children!.push(questionNode)
        currentSection.metadata!.questionCount!++
      } else {
        // Question outside of a section - add to root
        root.children!.push(questionNode)
      }
      continue
    }

    // RANGE LIST - allow template placeholders
    const rangeListMatch = trimmedLine.match(/^RANGE LIST\s+(.+?):/)
    if (rangeListMatch) {
      const listName = rangeListMatch[1].trim()
      const endLine = findElementEnd(i)

      // Count items and check for PLACEHOLDER (lines starting with numbers)
      let itemCount = 0
      let hasPlaceholderItem = false
      let j = i + 1
      while (j < lines.length && lines[j].trim().match(/^\d+:/)) {
        itemCount++
        // Check if this item contains PLACEHOLDER
        if (lines[j].toUpperCase().includes('PLACEHOLDER')) {
          hasPlaceholderItem = true
        }
        j++
      }

      const listNode: StructureNode = {
        id: `rangelist_${lineNumber}`,
        type: NodeType.RANGE_LIST,
        name: listName,
        lineNumber,
        endLineNumber: endLine + 1,
        metadata: {
          itemCount,
          referenceCount: 0,
          isPlaceholder: itemCount === 0 || listName.toUpperCase().includes('PLACEHOLDER') || hasPlaceholderItem,
        },
      }

      if (currentSection) {
        currentSection.children!.push(listNode)
      } else {
        root.children!.push(listNode)
      }
      allLists.push(listNode)
      continue
    }

    // LIST (but not RANGE LIST or LIST EXCEPTIONS) - allow template placeholders
    const listMatch = trimmedLine.match(/^LIST\s+(.+?)$/)
    if (listMatch && !trimmedLine.includes("RANGE") && trimmedLine !== "LIST EXCEPTIONS") {
      const listName = listMatch[1].trim()
      const endLine = findElementEnd(i)

      // Count items and check for PLACEHOLDER (non-empty lines until next keyword or comment)
      let itemCount = 0
      let hasPlaceholderItem = false
      let j = i + 1
      while (j < lines.length) {
        const nextLine = lines[j].trim()
        if (
          !nextLine ||
          nextLine.startsWith("#") ||
          nextLine.match(/^(LIST|RANGE LIST|SECTION|Q\s|TEXT|NOTE|RANDOMIZE)/)
        ) {
          break
        }
        itemCount++
        // Check if this item contains PLACEHOLDER
        if (nextLine.toUpperCase().includes('PLACEHOLDER')) {
          hasPlaceholderItem = true
        }
        j++
      }

      const listNode: StructureNode = {
        id: `list_${lineNumber}`,
        type: NodeType.LIST,
        name: listName,
        lineNumber,
        endLineNumber: endLine + 1,
        metadata: {
          itemCount,
          referenceCount: 0,
          isPlaceholder: itemCount === 0 || listName.toUpperCase().includes('PLACEHOLDER') || hasPlaceholderItem,
        },
      }

      if (currentSection) {
        currentSection.children!.push(listNode)
      } else {
        root.children!.push(listNode)
      }
      allLists.push(listNode)
      continue
    }

    // EXCEPTIONS
    if (trimmedLine === "EXCEPTIONS" || trimmedLine === "LIST EXCEPTIONS") {
      const items: ExceptionItem[] = []
      const endLine = findElementEnd(i)

      // Collect exception phrases
      let j = i + 1
      while (j < lines.length) {
        const nextLine = lines[j].trim()
        if (
          !nextLine ||
          nextLine.startsWith("#") ||
          nextLine.match(/^(LIST|RANGE LIST|SECTION|Q\s|TEXT|NOTE|RANDOMIZE)/)
        ) {
          break
        }

        // Remove [[open:1]] markers and clean up
        const cleanText = nextLine.replace(/\[\[open:\d+\]\]/g, "").trim()
        if (cleanText) {
          items.push({ text: cleanText, usageCount: 0, isUnused: false })
          exceptionsPhrases.push(cleanText)
        }
        j++
      }

      const exceptionsNode: StructureNode = {
        id: `exceptions_${lineNumber}`,
        type: NodeType.EXCEPTIONS,
        name: "EXCEPTIONS",
        lineNumber,
        endLineNumber: endLine + 1,
        children: [], // Will be populated with child nodes for each item
        metadata: {
          itemCount: items.length,
          referenceCount: 0,
          items,
        },
      }

      if (currentSection) {
        currentSection.children!.push(exceptionsNode)
      } else {
        root.children!.push(exceptionsNode)
      }
      allLists.push(exceptionsNode)
      continue
    }

    // TEXT - allow template placeholders
    const textMatch = trimmedLine.match(/^TEXT\s+(.+?):/)
    if (textMatch) {
      const textName = textMatch[1].trim()
      const endLine = findElementEnd(i)

      const textNode: StructureNode = {
        id: `text_${lineNumber}`,
        type: NodeType.TEXT,
        name: textName,
        lineNumber,
        endLineNumber: endLine + 1,
      }

      if (currentSection) {
        currentSection.children!.push(textNode)
        currentSection.metadata!.textBlockCount!++
      } else {
        root.children!.push(textNode)
      }
      continue
    }

    // NOTE - allow template placeholders
    const noteMatch = trimmedLine.match(/^NOTE\s+(.+?):/)
    if (noteMatch) {
      const noteName = noteMatch[1].trim()
      const endLine = findElementEnd(i)

      const noteNode: StructureNode = {
        id: `note_${lineNumber}`,
        type: NodeType.NOTE,
        name: noteName,
        lineNumber,
        endLineNumber: endLine + 1,
      }

      if (currentSection) {
        currentSection.children!.push(noteNode)
        currentSection.metadata!.noteCount!++
      } else {
        root.children!.push(noteNode)
      }
      continue
    }

    // LOGIC blocks (IF, BEGIN, FOR EACH, TERMINATE, etc.)
    const logicMatch = trimmedLine.match(/^(IF\s|BEGIN|END|TERMINATE|FOR EACH|RANDOMIZE|SKIP TO|VALIDATE|HIDE|HIDDEN)(.*)/)
    if (logicMatch) {
      const logicType = logicMatch[1].trim()
      const logicContent = logicMatch[2].trim()
      const endLine = findElementEnd(i)

      const logicNode: StructureNode = {
        id: `logic_${lineNumber}`,
        type: NodeType.LOGIC,
        name: logicContent ? `${logicType} ${logicContent}` : logicType,
        lineNumber,
        endLineNumber: endLine + 1,
      }

      if (currentSection) {
        currentSection.children!.push(logicNode)
      } else {
        root.children!.push(logicNode)
      }
      continue
    }
  }

  // Second pass: Calculate reference counts
  calculateReferenceCounts(allLists, content)

  // Create child nodes for EXCEPTIONS items
  allLists.forEach((list) => {
    if (list.type === NodeType.EXCEPTIONS && list.metadata?.items) {
      list.children = list.metadata.items.map((item, idx) => ({
        id: `exception_item_${list.lineNumber}_${idx}`,
        type: NodeType.EXCEPTION_ITEM, // Using EXCEPTION_ITEM type for exception items
        name: item.text,
        lineNumber: list.lineNumber + idx + 1,
        metadata: {
          referenceCount: item.usageCount,
          isUnused: item.isUnused,
        },
      }))
    }
  })

  // Third pass: Fix section end boundaries to include all children
  const fixSectionBoundaries = (node: StructureNode) => {
    if (node.type === NodeType.SECTION && node.children && node.children.length > 0) {
      // Find the last child's end line
      const lastChild = node.children[node.children.length - 1]
      const lastChildEnd = lastChild.endLineNumber || lastChild.lineNumber
      node.endLineNumber = lastChildEnd
    }

    // Recursively fix children
    if (node.children) {
      node.children.forEach((child) => fixSectionBoundaries(child))
    }
  }

  root.children?.forEach((child) => fixSectionBoundaries(child))

  return root
}

/**
 * Calculate reference counts for lists and exception phrases
 */
const calculateReferenceCounts = (
  lists: StructureNode[],
  content: string
): void => {
  // Count list references (FROM/PER)
  lists.forEach((list) => {
    if (list.type !== NodeType.EXCEPTIONS) {
      const escapedName = escapeRegex(list.name)
      const regex = new RegExp(`(FROM|PER)\\s+${escapedName}\\b`, "g")
      const matches = content.match(regex)
      list.metadata!.referenceCount = matches ? matches.length : 0
      list.metadata!.isUnused = list.metadata!.referenceCount === 0
    }
  })

  // Count individual EXCEPTIONS phrase usage
  const exceptionsNode = lists.find((l) => l.type === NodeType.EXCEPTIONS)
  if (exceptionsNode?.metadata?.items) {
    exceptionsNode.metadata.items.forEach((item) => {
      // Search for exact phrase matches (whole lines in SELECT statements)
      const escapedPhrase = escapeRegex(item.text)
      // Match the phrase as a complete line or after common prefixes
      const regex = new RegExp(`^${escapedPhrase}$`, "gm")
      const matches = content.match(regex)
      item.usageCount = matches ? matches.length : 0
      item.isUnused = item.usageCount === 0
    })

    // Total references = sum of all item usage
    exceptionsNode.metadata.referenceCount = exceptionsNode.metadata.items.reduce(
      (sum, item) => sum + item.usageCount,
      0
    )
  }
}

/**
 * Escape special regex characters
 */
const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}
